/**
 * Deep-link handler for the Capacitor-wrapped mobile app.
 *
 * Routes both flavours to React Router:
 *   - Universal Link / App Link : https://unityblaze.com/r/<token>
 *   - Custom scheme             : unityblaze://r/<token>
 *
 * The native bridge fires `appUrlOpen` for both COLD start (app launched
 * from a link) and WARM resume (app already running, link tapped). We just
 * call `navigate()` — React Router handles the rest. Existing routes are
 * unchanged; this is purely an additional entry point.
 *
 * SAFE on web: web is a regular browser context where the URL is already in
 * the address bar, so this initializer is a no-op via `isNative()`.
 *
 * USAGE: called from `App.js` inside the `<BrowserRouter>` tree so the
 * `navigate` instance is bound to React Router context.
 */
import { App as CapacitorApp } from '@capacitor/app';

import { isNative } from '@/native/permissions';
import { closeAuthBrowser } from '@/native/googleAuth';

let listenerHandle = null;

/**
 * Convert a deep-link URL to the React Router path that should be navigated to.
 * Returns null when the URL is not understood so the caller can no-op safely.
 *
 * Examples:
 *   unityblaze://r/abc123        → /r/abc123
 *   unityblaze://r/abc123?x=1    → /r/abc123?x=1
 *   https://unityblaze.com/r/abc → /r/abc
 *   https://unityblaze.com/      → /
 *   garbage                      → null
 */
export function deepLinkToPath(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    const url = new URL(rawUrl);
    // unityblaze:// — host is the first path segment, pathname is the rest.
    if (url.protocol === 'unityblaze:') {
      const host = url.host || '';
      const tail = url.pathname || '';
      const path = '/' + host + tail;
      return path.replace(/\/+/g, '/') + (url.search || '');
    }
    // https://<our-domain>/... — keep pathname + search, drop origin.
    if (url.protocol === 'https:' || url.protocol === 'http:') {
      return (url.pathname || '/') + (url.search || '');
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to native deep-link events and navigate via React Router.
 * Returns an `unsubscribe()` function for the caller to call on unmount.
 */
export async function initDeepLinks(navigate) {
  if (!isNative()) return () => {};
  if (listenerHandle) {
    // Already initialised — defensive guard against StrictMode double-mount.
    return () => {
      listenerHandle?.remove();
      listenerHandle = null;
    };
  }

  try {
    listenerHandle = await CapacitorApp.addListener('appUrlOpen', (event) => {
      const path = deepLinkToPath(event?.url);
      if (path) {
        // If we're coming back from Google OAuth, the in-app Safari/Chrome
        // sheet is still on top of our app — dismiss it before navigating
        // so the user lands on the React screen, not the browser.
        if (path.startsWith('/auth/native-callback')) {
          closeAuthBrowser();
        }
        navigate(path);
      }
    });
  } catch (e) {
    console.warn('initDeepLinks failed:', e);
  }

  return () => {
    listenerHandle?.remove();
    listenerHandle = null;
  };
}
